import { ErrorState } from "$components/error-state";
import { LoadingLogo } from "$components/loading/loading-logo";
import { toast } from "$global-providers/toast-provider";
import { gql } from "@apollo/client";
import { Box, Grid } from "@material-ui/core";
import { useIntl } from "react-intl";
import { useInsuranceProvidersQuery } from "./__generated__/TabContentOnline.gql";
import { InvoiceCard } from "./components";
import EmptySearchState from "./components/EmptySearchState";
import { useFetchPatientInvoices } from "./components/hooks/useFetchPatientInvoices";
import useFetchPriceList from "./components/hooks/useFetchPriceListV2";
import useFilteredInvoices from "./components/hooks/useFilteredInvoices";
import { InvoiceFilters } from "./components/InvoiceFilters";
import InvoicingEmptyState from "./components/InvoicingEmptyState";
import { capitaliseDependentsEnum } from "./components/insurers";

gql`
  query InsuranceProviders {
    minInsuranceProviders {
      id
      name
      insuranceType
      properties
    }
  }
`;

export const TabContentOnline: FC<{ patientId: string }> = ({ patientId }) => {
  const { formatMessage } = useIntl();

  const { priceList } = useFetchPriceList();
  const { data: insurersData, loading: insurersLoading } =
    useInsuranceProvidersQuery({
      fetchPolicy: "cache-and-network",
      onError() {
        toast.error(
          formatMessage({
            id: "invoicing.getInsurers.error.msg",
            defaultMessage: "Failed to fetch the insurers for this territory",
          }),
        );
      },
    });
  const { invoices, loading, error } = useFetchPatientInvoices(patientId);
  const { filteredInvoices, ...invoiceFilters } = useFilteredInvoices(invoices);

  if ((loading && invoices.length === 0) || insurersLoading)
    return <LoadingLogo />;

  if (error)
    return (
      <Box p={3}>
        <ErrorState variant="inline" error={error} />
      </Box>
    );

  if (!loading && invoices.length === 0) {
    return <InvoicingEmptyState />;
  }

  // Grim, is to convert the lowercase enum values for 'dependents' sent by api-insurance to the
  // uppercase values in the gql Dependent enum that is used everywhere else including submission
  const insurers = capitaliseDependentsEnum(
    insurersData?.minInsuranceProviders ?? [],
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <InvoiceFilters {...invoiceFilters} />
      </Grid>
      {filteredInvoices.length < 1 ? (
        <EmptySearchState />
      ) : (
        filteredInvoices?.map((invoice) => (
          <Grid item xs={12} key={invoice.invoiceId}>
            <InvoiceCard
              {...invoice}
              priceList={priceList}
              // @ts-expect-error Unfortunate cast as api-insurance returns a JsonObject for the properties field
              insurers={insurers}
              balance={invoice.invoiceSummary.balance}
            />
          </Grid>
        ))
      )}
    </Grid>
  );
};
